import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, Injector, NgModule, } from '@angular/core';
import { createCustomElement } from '@angular/elements';

// Modules
import { BrowserModule } from '@angular/platform-browser';
import { CoreModule } from '@core/core.module';
import { FeatureToggleModule, SecurityModule, SecurityService, } from '@mynexus/mynexus-ui-lib';

// Services
import { ConfigurationService } from '@core/service';
import { HHMfeService } from '@core/service/hh-mfe.service';

// Components
import { MncHHComponent } from './mnc-hh.component';
import { EmptyComponent } from '@shared/empty/empty.component';

import { environment } from '@env/environment';
import { PrimeNGConfigLoader, providePrimeNGFromShell, RouteInterceptor, ShellService, TokenInterceptor } from '@mynexus/mfe-core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

/**
 * MFE-specific default theme loader. Lazy-loads `@mynexus/theme` so the Carelon
 * preset is only fetched when the shell hasn't supplied its own theme.
 */
const loadCarelonPrimeNGConfig: PrimeNGConfigLoader = async () => {
  const { Carelon, CSS_LAYERS_ORDER } = await import('@mynexus/theme');

  return {
    ripple: false,
    theme: {
      preset: Carelon,
      options: {
        darkModeSelector: false,
        cssLayer: {
          name: 'primeng',
          order: CSS_LAYERS_ORDER
        }
      },
    }
  };
}

function loadConfig(shellService: ShellService, securityService: SecurityService) {
  return async () => {
    const permissions = shellService.authenticationService.userPermissions;
    securityService.setUserPermissions(permissions.flat());

    // Legacy permissions model force us to set this false to avoid problem with empty permissions users
    securityService.setAllowEmptyPermission(false);
  };
}

@NgModule({
  declarations: [
    MncHHComponent,
    EmptyComponent,
  ],
  imports: [
    BrowserModule,
    CoreModule,
    FeatureToggleModule,
    FeatureToggleModule.forRoot(environment.apiUrl),
    SecurityModule.forRoot([], environment.apiUrl),
  ],
  exports: [
    ...environment.modules,
  ],
  providers: [
    providePrimeNGFromShell(loadCarelonPrimeNGConfig),
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfig,
      deps: [ShellService, SecurityService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RouteInterceptor,
      multi: true
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MncHHModule implements DoBootstrap {
  constructor(
    private injector: Injector,
    private shellService: ShellService,
    private mfeService: HHMfeService
  ) {
  }

  ngDoBootstrap(): void {
    this.registerCustomElement('mnc-hh', MncHHComponent);

    this.shellService.registerMfeService('mnc-hh', this.mfeService);
  }

  private registerCustomElement(name: string, component: any) {
    if (customElements.get(name)) return;
    customElements.define(name, createCustomElement<any>(component, {injector: this.injector}));
  }
}
